<template>
  <div class="content-wrapper recap-board">
    <div class="content-body">

      <!-- HEADER -->
      <header class="recap-board__head">
        <div>
          <p class="recap-board__eyebrow">
            Chantiers qualité
          </p>
          <h1 class="recap-board__title">
            Récap des chantiers
          </h1>
        </div>
        <router-link
          :to="{ name: 'chantiers-list' }"
          class="recap-board__back"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            size="14"
            class="mr-50"
          />
          Liste des chantiers
        </router-link>
      </header>

      <!-- FILTRE PROJET -->
      <div class="recap-board__filter">
        <b-form-group
          label="Projet"
          label-for="recap-filter-projet"
          class="mb-0"
        >
          <b-form-select
            id="recap-filter-projet"
            v-model="filtreProjet"
            :options="projetOptions"
          />
        </b-form-group>
        <p
          v-if="projetSelectionne && projetSelectionne.description"
          class="recap-board__projet-desc mt-1 mb-0"
        >
          {{ projetSelectionne.description }}
        </p>
      </div>

      <!-- LOADING -->
      <div
        v-if="loading"
        class="recap-board__state"
      >
        <b-spinner class="mb-1" />
        <p class="mt-1 mb-0">
          Chargement du récapitulatif…
        </p>
      </div>

      <!-- ERROR -->
      <div
        v-else-if="error"
        class="recap-board__state recap-board__state--error"
      >
        <feather-icon
          icon="AlertTriangleIcon"
          size="32"
        />
        <p class="mt-1 mb-1">
          {{ error }}
        </p>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="load"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
            class="mr-50"
          />
          Réessayer
        </b-button>
      </div>

      <!-- BOARD -->
      <div
        v-else
        class="recap-board__columns"
      >
        <section
          v-for="col in board.colonnes"
          :key="col.statutId"
          class="recap-col"
        >
          <header class="recap-col__head">
            <span
              class="recap-col__dot"
              :style="{ backgroundColor: col.couleur || '#5A7373' }"
            />
            <span class="recap-col__title">{{ col.libelle }}</span>
            <span class="recap-col__count">{{ col.total }}</span>
          </header>

          <p
            v-if="col.items.length === 0"
            class="recap-col__empty"
          >
            {{ col.estTerminal ? 'Rien sur les 90 derniers jours.' : 'Aucun chantier.' }}
          </p>

          <article
            v-for="c in col.items"
            :key="c.id"
            class="recap-card"
            @click="goToDetail(c)"
          >
            <span
              class="badge recap-card__cat"
              :style="{ backgroundColor: c.categorieCouleur || '#5A7373', color: '#fff' }"
            >{{ c.categorieLibelle }}</span>

            <h3 class="recap-card__name">
              {{ c.nom }}
            </h3>
            <p class="recap-card__sub">
              {{ c.sousCategorie || c.origineLibelle }}
            </p>

            <p
              v-if="col.estTerminal && c.dateStatutActuel"
              class="recap-card__date"
            >
              <feather-icon
                icon="CheckCircleIcon"
                size="12"
                class="mr-25"
              />
              {{ col.libelle }} le {{ formatDate(c.dateStatutActuel) }}
            </p>
            <p
              v-else-if="c.dateEcheance"
              class="recap-card__date"
            >
              <feather-icon
                icon="CalendarIcon"
                size="12"
                class="mr-25"
              />
              Échéance {{ formatDate(c.dateEcheance) }}
            </p>

            <div class="recap-card__footer">
              <span class="recap-card__metric">
                <feather-icon
                  icon="TargetIcon"
                  size="13"
                  class="mr-25"
                />
                {{ c.nbPlansAction }}
              </span>
              <span class="recap-card__metric">
                <feather-icon
                  icon="UsersIcon"
                  size="13"
                  class="mr-25"
                />
                {{ c.nbParticipants }}
              </span>
            </div>
          </article>
        </section>
      </div>

    </div>
  </div>
</template>

<script>
import {
  BButton, BSpinner, BFormGroup, BFormSelect,
} from 'bootstrap-vue'

export default {
  name: 'ChantiersRecapPublicView',
  components: {
    BButton,
    BSpinner,
    BFormGroup,
    BFormSelect,
  },
  data() {
    return {
      board: { colonnes: [] },
      loading: true,
      error: null,
      // Filtre par projet d'amélioration continue (initialisé depuis la query, ex. clic depuis un projet).
      filtreProjet: (this.$route && this.$route.query && this.$route.query.projetId) || null,
      projets: [],
    }
  },
  computed: {
    projetOptions() {
      return [
        { value: null, text: 'Tous les projets' },
        ...this.projets.map(p => ({ value: p.id, text: p.nom })),
      ]
    },
    // Projet actuellement filtré (pour afficher sa description sous le titre, réactif).
    projetSelectionne() {
      return this.projets.find(p => p.id === this.filtreProjet) || null
    },
  },
  watch: {
    filtreProjet(val) {
      this.syncProjetQuery(val)
      this.load()
    },
  },
  async created() {
    await this.loadProjets()
    await this.load()
  },
  methods: {
    async loadProjets() {
      try {
        this.projets = (await this.$http.myressif.chantierProjets.listRef()) || []
      } catch (e) {
        this.projets = []
      }
    },
    async load() {
      this.loading = true
      this.error = null
      try {
        const board = await this.$http.myressif.chantiers.recapPublic(this.filtreProjet || undefined)
        this.board = (board && board.colonnes) ? board : { colonnes: [] }
      } catch (e) {
        this.board = { colonnes: [] }
        this.error = 'Une erreur est survenue lors du chargement du récapitulatif.'
      } finally {
        this.loading = false
      }
    },
    // Garde l'URL en phase avec le filtre projet (pose/retire ?projetId sans recharger la route).
    syncProjetQuery(val) {
      if ((this.$route.query.projetId || null) === (val || null)) return
      const query = { ...this.$route.query }
      if (val) query.projetId = val
      else delete query.projetId
      this.$router.replace({ query }).catch(() => {})
    },
    goToDetail(c) {
      this.$router.push({ name: 'chantier-detail', params: { id: c.id } })
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleDateString('fr-FR')
    },
  },
}
</script>

<style scoped lang="scss">
$at-green:   #1c4d4d;
$at-orange:  #ff6131;
$at-ink-900: #0F1F1F;
$at-ink-700: #2A3F3F;
$at-ink-500: #5A7373;
$at-ink-100: #E4ECEC;

.recap-board {
  &__head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;
  }
  &__eyebrow {
    color: $at-green;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: .08em;
    text-transform: uppercase;
    margin: 0 0 4px;
  }
  &__title {
    font-family: 'Iowan Old Style', 'Palatino Linotype', Georgia, serif;
    font-size: 26px;
    color: $at-green;
    font-weight: 500;
    margin: 0;
  }
  &__filter {
    max-width: 420px;
    margin-bottom: 20px;
  }
  &__projet-desc {
    color: $at-ink-500;
    font-size: 13px;
    white-space: pre-line;
  }
  &__back {
    display: inline-flex;
    align-items: center;
    color: $at-ink-500;
    font-size: 13px;
    text-decoration: none;
    &:hover { color: $at-orange; }
  }
  &__state {
    text-align: center;
    padding: 48px 16px;
    color: $at-ink-500;
    background: #fff;
    border: 1px dashed $at-ink-100;
    border-radius: 10px;
    &--error { color: $at-orange; border-color: rgba(255, 97, 49, .3); }
  }
  &__columns {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
  }
}

.recap-col {
  background: #F2F6F6;
  border: 1px solid $at-ink-100;
  border-radius: 12px;
  padding: 12px;
  min-height: 120px;

  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding: 0 2px;
  }
  &__dot { width: 10px; height: 10px; border-radius: 50%; flex: 0 0 auto; }
  &__title { font-weight: 600; color: $at-ink-900; font-size: 14px; }
  &__count {
    margin-left: auto;
    background: #fff;
    border: 1px solid $at-ink-100;
    border-radius: 999px;
    padding: 1px 9px;
    font-size: 12px;
    color: $at-ink-700;
  }
  &__empty {
    color: $at-ink-500;
    font-size: 12px;
    font-style: italic;
    padding: 8px 4px;
    margin: 0;
  }
}

.recap-card {
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;

  &:hover {
    border-color: $at-green;
    box-shadow: 0 4px 14px rgba(28, 77, 77, .08);
    transform: translateY(-1px);
  }
  &:last-child { margin-bottom: 0; }

  &__cat {
    font-size: 10px;
    font-weight: 600;
    padding: 4px 9px;
    border-radius: 999px;
  }
  &__name {
    font-size: 14px;
    color: $at-ink-900;
    font-weight: 600;
    margin: 8px 0 2px;
    line-height: 1.3;
  }
  &__sub {
    color: $at-orange;
    font-size: 12px;
    font-weight: 500;
    margin: 0 0 8px;
  }
  &__date {
    display: inline-flex;
    align-items: center;
    color: $at-ink-500;
    font-size: 12px;
    margin: 0 0 8px;
  }
  &__footer {
    display: flex;
    align-items: center;
    gap: 14px;
    padding-top: 8px;
    border-top: 1px solid $at-ink-100;
  }
  &__metric {
    display: inline-flex;
    align-items: center;
    color: $at-ink-500;
    font-size: 12px;
  }
}

@media (max-width: 1100px) {
  .recap-board__columns { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 640px) {
  .recap-board__columns { grid-template-columns: 1fr; }
}
</style>
